import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { createMonitoringRequest } from './http';

export interface WeatherObservation {
  reference: string;
  sourceMode: 'LIVE';
  regionId: string;
  latitude: number | null;
  longitude: number | null;
  locationStatus: 'LOCATED' | 'UNLOCATED';
  receivedAt: string;
  input: {
    sourceId: string;
    observedAt: string;
    metric: string;
    value: number;
    unit: string;
    quality: string;
    synthetic: false;
  };
  provenance: {
    provider: string;
    externalObservationId: string;
    rawSha256: string;
    providerQuality: string;
    ingestionRevision: string;
  };
}
export interface WeatherSnapshot {
  sourceMode: 'LIVE';
  generatedAt: string;
  total: number;
  observations: WeatherObservation[];
  sources: { sourceId: string; region: { areaId: string; displayName: string } }[];
  health: {
    sourceId: string;
    status: 'CONNECTED' | 'DEGRADED' | 'UNAVAILABLE' | 'STALE';
    lastProviderErrorCategory: string | null;
    consecutiveFailures: number;
    nextRetryAt: string | null;
    quality: string;
  }[];
}
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const time = (v: unknown) =>
  typeof v === 'string' && /Z$/.test(v) && Number.isFinite(Date.parse(v));
export function parseWeather(value: unknown): WeatherSnapshot {
  if (
    !object(value) ||
    value.sourceMode !== 'LIVE' ||
    !time(value.generatedAt) ||
    !Number.isSafeInteger(value.total) ||
    Number(value.total) < 0 ||
    !Array.isArray(value.observations) ||
    value.observations.length > 30 ||
    !Array.isArray(value.sources) ||
    value.sources.length > 10 ||
    !Array.isArray(value.health)
  )
    throw new Error('WEATHER_WIRE');
  const sources = new Set<string>();
  for (const s of value.sources) {
    if (
      !object(s) ||
      typeof s.sourceId !== 'string' ||
      sources.has(s.sourceId) ||
      !object(s.region) ||
      typeof s.region.areaId !== 'string' ||
      typeof s.region.displayName !== 'string'
    )
      throw new Error('WEATHER_SOURCE');
    sources.add(s.sourceId);
  }
  for (const o of value.observations) {
    if (
      !object(o) ||
      o.sourceMode !== 'LIVE' ||
      !object(o.input) ||
      o.input.synthetic !== false ||
      typeof o.reference !== 'string' ||
      typeof o.regionId !== 'string' ||
      !sources.has(String(o.input.sourceId)) ||
      !time(o.receivedAt) ||
      !time(o.input.observedAt) ||
      typeof o.input.value !== 'number' ||
      !Number.isFinite(o.input.value) ||
      !['TEMPERATURE', 'RELATIVE_HUMIDITY', 'WIND_SPEED'].includes(String(o.input.metric)) ||
      typeof o.input.unit !== 'string' ||
      !['GOOD', 'UNVERIFIED'].includes(String(o.input.quality)) ||
      !object(o.provenance) ||
      o.provenance.provider !== 'NOAA_NWS' ||
      typeof o.provenance.rawSha256 !== 'string' ||
      !/^[a-f0-9]{64}$/.test(o.provenance.rawSha256)
    )
      throw new Error('WEATHER_OBSERVATION');
    if (o.locationStatus === 'UNLOCATED') {
      if (o.latitude !== null || o.longitude !== null) throw new Error('WEATHER_LOCATION');
    } else if (
      o.locationStatus !== 'LOCATED' ||
      typeof o.latitude !== 'number' ||
      typeof o.longitude !== 'number' ||
      !Number.isFinite(o.latitude) ||
      !Number.isFinite(o.longitude) ||
      Math.abs(o.latitude) > 90 ||
      Math.abs(o.longitude) > 180
    )
      throw new Error('WEATHER_LOCATION');
  }
  const healthIds = new Set<string>();
  for (const h of value.health) {
    if (
      !object(h) ||
      !sources.has(String(h.sourceId)) ||
      healthIds.has(String(h.sourceId)) ||
      !['CONNECTED', 'DEGRADED', 'UNAVAILABLE', 'STALE'].includes(String(h.status)) ||
      !Number.isSafeInteger(h.consecutiveFailures) ||
      Number(h.consecutiveFailures) < 0 ||
      typeof h.quality !== 'string' ||
      !(h.nextRetryAt === null || time(h.nextRetryAt)) ||
      !(h.lastProviderErrorCategory === null || typeof h.lastProviderErrorCategory === 'string')
    )
      throw new Error('WEATHER_HEALTH');
    healthIds.add(String(h.sourceId));
  }
  if (healthIds.size !== sources.size) throw new Error('WEATHER_COVERAGE');
  return value as unknown as WeatherSnapshot;
}
export function readWeather(signal: AbortSignal): Promise<WeatherSnapshot> {
  return createMonitoringRequest({ baseOrigin: apiBaseOrigin() })(
    '/read-models/normal-monitoring/weather?sourceMode=LIVE',
    parseWeather,
    signal,
  );
}
